import { Box, Icon, type IconProps } from '@customerio/pluma-components/react';
import { DATA_SOURCE_TYPE_ICON } from '@/utils/data-source-icons';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

/**
 * An icon and a name, identifying one thing a condition or trigger reads: an
 * attribute, an event, a form, a segment, an object or relationship
 * attribute, an inbound channel — or, with no icon, a value it compares
 * against.
 *
 * **This component resolves nothing.** Every name arrives already resolved,
 * which is what lets it live under `components/`:
 * `local-rules/enforce-feature-boundaries` forbids importing `features/` or
 * `services/` from here, so a lookup is not available even in principle. The
 * caller owns the lookup, and a caller that cannot render a node component
 * that fetches — a React Flow node, rebuilt from its `nodeTypes` map — resolves
 * the name at the page and threads it down.
 *
 * An unresolved name is a first-class state rather than an empty string: the
 * label dims and says what is missing, so a form whose list has not loaded
 * reads as `select form` instead of an icon with nothing beside it.
 */

/** The data source kinds this label renders. */
export type DataSourceLabelType =
  | 'attribute'
  | 'value'
  | 'event'
  | 'inbound'
  | 'form'
  | 'segment'
  | 'objectType'
  | 'objectAttribute'
  | 'relationshipAttribute';

/**
 * What a label reads when its name could not be resolved.
 *
 * Worded per kind rather than shared, because the two reasons a name is absent
 * read differently: a thing the user has not chosen yet invites a choice
 * (`select event`), while a thing that should exist but did not resolve reports
 * the gap (`missing object attribute`).
 */
const MISSING_LABEL = {
  attribute: 'missing attribute',
  // An empty value reads as the empty string it is, as a condition writes it.
  value: '""',
  event: 'select event',
  inbound: 'select inbound event',
  form: 'select form',
  segment: 'select segment',
  objectType: 'missing object type',
  objectAttribute: 'missing object attribute',
  relationshipAttribute: 'missing relationship attribute',
} satisfies Record<DataSourceLabelType, string>;

/**
 * The icon for each kind, drawn from the shared map.
 *
 * Only the indirection is here: the icon names themselves live in
 * `@/utils/data-source-icons`, so this adds no second copy of them. The two
 * attribute kinds map onto the map's own `object` and `relationship` keys,
 * which is why this is a mapping rather than a direct index.
 */
const TYPE_ICON = {
  attribute: DATA_SOURCE_TYPE_ICON.attribute,
  // A value is the one kind with no marker: it is what a condition compares
  // against, not a thing it reads.
  value: null,
  event: DATA_SOURCE_TYPE_ICON.event,
  inbound: DATA_SOURCE_TYPE_ICON.inbound,
  form: DATA_SOURCE_TYPE_ICON.form,
  segment: DATA_SOURCE_TYPE_ICON.segment,
  objectType: DATA_SOURCE_TYPE_ICON.object,
  objectAttribute: DATA_SOURCE_TYPE_ICON.object,
  relationshipAttribute: DATA_SOURCE_TYPE_ICON.relationship,
} satisfies Record<DataSourceLabelType, IconProps['name'] | null>;

export type DataSourceLabelProps = {
  /** Which kind of data source this names. */
  type: DataSourceLabelType;
  /**
   * The resolved name, or nullish when it could not be resolved.
   *
   * Nullish and blank are treated the same: both render the missing state,
   * because a label showing a name of spaces is indistinguishable from a
   * broken one.
   */
  name?: string | null;
  /**
   * An icon overriding the one the kind implies, for a kind whose icon varies
   * with the thing itself — a form's icon follows its form type, and an inbound
   * event's follows its channel.
   */
  icon?: IconProps['name'];
  /**
   * How the label is drawn: `plain` is the icon and name alone; `bordered` and
   * `bordered-subtle` draw it as a pill with its own border and padding, filled
   * with the base or the subtle surface color.
   */
  variant?: DataSourceLabelVariant;
  /** Class applied to the label's root, so a caller can position it. */
  className?: string;
};

/** The ways a label can be drawn. */
export type DataSourceLabelVariant = 'plain' | 'bordered' | 'bordered-subtle';

const VARIANT_CLASS_NAME = {
  plain: null,
  bordered: styles.bordered,
  'bordered-subtle': styles.borderedSubtle,
} satisfies Record<DataSourceLabelVariant, string | null>;

export function DataSourceLabel({ type, name, icon, variant = 'plain', className }: DataSourceLabelProps) {
  const isMissing = isEmptyString(name);
  const label = isMissing ? MISSING_LABEL[type] : name;
  const iconName = icon ?? TYPE_ICON[type];
  const containerClassName = [
    styles.container,
    VARIANT_CLASS_NAME[variant],
    isMissing ? styles.missing : null,
    className,
  ]
    .filter((value) => value != null)
    .join(' ');

  return (
    <Box as="span" className={containerClassName} data-testid={`data-source-label-${type}`}>
      {iconName == null ? null : (
        <Box as="span" className={styles.iconContainer}>
          <Icon name={iconName} size="sm" color="subtle" />
        </Box>
      )}
      <Box as="span" truncate>
        {label}
      </Box>
    </Box>
  );
}
